import { useEffect, useState } from 'react'
import { Route, Routes, useLocation, useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from './lib/api'
import { realtime } from './lib/realtime'
import { useMessages } from './lib/store'
import { ConversationList } from './components/ConversationList'
import { Conversation } from './components/Conversation'
import { AddContact } from './components/AddContact'
import { NewGroup } from './components/NewGroup'
import { SearchOverlay } from './components/SearchOverlay'
import { StarredView } from './components/StarredView'
import { cn } from './lib/utils'

/** ConversationRoute reads the id from the URL so a conversation is linkable
 *  and survives a reload — the messenger equivalent of a permalink. */
function ConversationRoute() {
  const { id } = useParams<{ id: string }>()
  if (!id) return <EmptyState />
  return <Conversation conversationId={id} />
}

function EmptyState() {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-2 p-8">
      <p className="text-lg font-medium">Pick a contact</p>
      <p className="max-w-sm text-center text-muted-foreground">
        Every contact is an agent reachable at a URL. Add one to start talking.
      </p>
    </div>
  )
}

export function App() {
  const applyEnvelope = useMessages((s) => s.applyEnvelope)
  const queryClient = useQueryClient()
  const [connected, setConnected] = useState(false)
  const [addOpen, setAddOpen] = useState(false)
  const [groupOpen, setGroupOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const [starredOpen, setStarredOpen] = useState(false)
  // Read the open conversation from the location rather than useParams: App is
  // rendered above <Routes>, so no route has matched here yet and useParams
  // would always be empty.
  const conversationOpen = useLocation().pathname.startsWith('/c/')

  const { data: me } = useQuery({ queryKey: ['me'], queryFn: api.me, retry: false })

  useEffect(() => {
    realtime.onStatus = setConnected

    const unsubscribe = realtime.subscribe((env) => {
      applyEnvelope(env)

      // A finished run changes the contact list — last message, ordering, and
      // the unread badge — so the list is refetched rather than kept in sync by
      // hand. It is one small query and the socket already told us exactly when
      // it is worth making.
      if (env.type === 'message.done' || env.type === 'message.created') {
        queryClient.invalidateQueries({ queryKey: ['conversations'] })
      }
    })

    realtime.connect()
    return () => {
      unsubscribe()
      realtime.close()
    }
  }, [applyEnvelope, queryClient])

  return (
    <div className="flex h-[100dvh] flex-col">
      {!connected && (
        <div className="bg-amber-500/15 px-4 py-1 text-center text-sm text-amber-700 dark:text-amber-400">
          Reconnecting… your agents keep working while you are offline.
        </div>
      )}

      <div className="flex min-h-0 flex-1">
        {/* On a phone the list and the conversation are one pane each: the list
            is hidden once a conversation is open, which is the single-pane
            responsive behaviour every messenger uses. */}
        <div
          className={cn(
            'min-h-0 w-full md:block md:w-[340px] md:border-r',
            conversationOpen ? 'hidden md:block' : 'block',
          )}
        >
          <ConversationList
            me={me}
            onAddContact={() => setAddOpen(true)}
            onNewGroup={() => setGroupOpen(true)}
            onSearch={() => setSearchOpen(true)}
            onStarred={() => setStarredOpen(true)}
          />
        </div>

        <div
          className={cn(
            'min-h-0 min-w-0 flex-1 md:flex',
            conversationOpen ? 'flex' : 'hidden md:flex',
          )}
        >
          <Routes>
            <Route path="/" element={<EmptyState />} />
            <Route path="/c/:id" element={<ConversationRoute />} />
          </Routes>
        </div>
      </div>

      <AddContact open={addOpen} onClose={() => setAddOpen(false)} />
      <NewGroup open={groupOpen} onClose={() => setGroupOpen(false)} />
      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
      <StarredView open={starredOpen} onClose={() => setStarredOpen(false)} />
    </div>
  )
}
